// Filename: SitemapPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Briefcase, Users, FileText, Shield, Cookie, Map, MessageSquare, ExternalLink, User } from 'lucide-react';

const SitemapPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const siteStructure = [
        {
            title: 'Main Pages',
            icon: <Home className="w-5 h-5" />,
            pages: [
                { name: 'Home', path: '/', description: 'Welcome to Appsytec - connecting talent with opportunity' },
                { name: 'About Us', path: '/about', description: 'Who we are, our mission, vision and social responsibility' },
                { name: 'Services', path: '/services', description: 'Our comprehensive ICT recruitment and consulting services' },
                { name: 'Available Candidates', path: '/candidates', description: 'Vetted ICT specialists currently available for work' },
                { name: 'Job Board', path: '/jobs', description: 'Browse and apply for current job opportunities' },
                { name: 'Contact', path: '/contact', description: 'Get in touch with our team' },
            ]
        },
        {
            title: 'Client & Candidate Portals',
            icon: <Users className="w-5 h-5" />,
            pages: [
                { name: 'Client Portal', path: '/login', description: 'Secure access for clients to manage projects and reports' },
                { name: 'Candidate Portal', path: '/login', description: 'Join our talent network and explore opportunities' },
            ]
        },
        {
            title: 'Legal & Compliance',
            icon: <Shield className="w-5 h-5" />,
            pages: [
                { name: 'Privacy Policy', path: '/privacy-policy', description: 'How we collect, use, and protect your personal information' },
                { name: 'Cookie Policy', path: '/cookie-policy', description: 'Information about cookies and tracking technologies we use' },
                { name: 'Modern Slavery Statement', path: '/modern-slavery-statement', description: 'Our commitment to preventing modern slavery in our operations' },
            ]
        },
        {
            title: 'Feedback & Communication',
            icon: <MessageSquare className="w-5 h-5" />,
            pages: [
                { name: 'Testimonials', path: '/testimonials', description: 'Share your experience working with Appsytec' },
                { name: 'Contact Us', path: '/contact', description: 'Get in touch with our team' },
            ]
        },
        {
            title: 'Site Information',
            icon: <Map className="w-5 h-5" />,
            pages: [
                { name: 'Sitemap', path: '/sitemap', description: 'Complete overview of our website structure' },
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/appsytec-logo.png"
                            alt="Appsytec Logo"
                            className="h-10 w-auto"
                        />
                    </Link>
                    <Link to="/" className="text-gray-700 font-medium hover:text-red-500 transition-colors">
                        Home
                    </Link>
                </div>
            </nav>

            <main className="flex-grow pt-24 pb-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Sitemap</h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Navigate through all the pages and sections of our website
                        </p>
                    </div>

                    <div className="space-y-12">
                        {siteStructure.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="bg-white rounded-2xl shadow-lg p-8">
                                <div className="flex items-center mb-6">
                                    <div className="bg-red-500 p-3 rounded-full text-white mr-4">
                                        {section.icon}
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {section.pages.map((page, pageIndex) => (
                                        <div key={pageIndex} className="border border-gray-200 rounded-lg p-6 hover:border-red-300 hover:shadow-md transition-all duration-200">
                                            <div className="flex items-start justify-between mb-3">
                                                <h3 className="text-lg font-semibold text-gray-900">{page.name}</h3>
                                                {page.path.startsWith('http') || page.path.startsWith('#') ? (
                                                    <a
                                                        href={page.path}
                                                        className="text-red-500 hover:text-red-600 transition-colors"
                                                        target={page.path.startsWith('http') ? '_blank' : '_self'}
                                                        rel={page.path.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                    >
                                                        <ExternalLink className="w-5 h-5" />
                                                    </a>
                                                ) : (
                                                    <Link
                                                        to={page.path}
                                                        className="text-red-500 hover:text-red-600 transition-colors"
                                                    >
                                                        <ExternalLink className="w-5 h-5" />
                                                    </Link>
                                                )}
                                            </div>
                                            <p className="text-gray-600 text-sm mb-3">{page.description}</p>
                                            <div className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-1 rounded">
                                                {page.path}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-16 bg-gray-50 rounded-2xl p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Actions</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Link
                                to="/"
                                className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:border-red-300 hover:shadow-md transition-all duration-200"
                            >
                                <Home className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                <span className="text-sm font-medium text-gray-700">Return Home</span>
                            </Link>
                            <Link
                                to="/services"
                                className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:border-red-300 hover:shadow-md transition-all duration-200"
                            >
                                <Briefcase className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                <span className="text-sm font-medium text-gray-700">View Services</span>
                            </Link>
                            <Link
                                to="/contact"
                                className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:border-red-300 hover:shadow-md transition-all duration-200"
                            >
                                <MessageSquare className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                <span className="text-sm font-medium text-gray-700">Contact Us</span>
                            </Link>
                            <Link
                                to="/testimonials"
                                className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:border-red-300 hover:shadow-md transition-all duration-200"
                            >
                                <User className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                <span className="text-sm font-medium text-gray-700">Leave Feedback</span>
                            </Link>
                        </div>
                    </div>

                    {/* About This Sitemap */}
                    <div className="mt-12 text-center">
                        <div className="bg-blue-50 rounded-lg p-6 max-w-2xl mx-auto">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">About This Sitemap</h3>
                            <p className="text-gray-600 text-sm">
                                This sitemap provides an overview of all publicly accessible pages on our website.
                                If you're having trouble finding specific information, please don't hesitate to{' '}
                                <Link to="/contact" className="text-red-500 hover:text-red-600 underline">
                                    contact us
                                </Link>{' '}
                                for assistance.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                            <span className="ml-2 text-xl font-bold">Appsytec Pty Ltd</span>
                        </div>
                        <p className="text-gray-400 mb-4">© 2025 Appsytec Pty Ltd. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SitemapPage;